import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { Chip, Divider, FormControl, FormHelperText, InputAdornment, InputLabel, MenuItem, OutlinedInput, Paper, Select, Stack, Typography } from '@mui/material'
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import ReductionActionService from '../../../shared/services/reduction-action/reduction-action.service'
import { IRecommendation, Scope2Basis } from '../../../shared/services/reduction-action/reduction-action.model'
import { costPerTonnePerYear, formatCostPerTonne, formatGbpRange, formatPaybackRange, formatTonnesRange, SCOPE2_BASIS_HELP, SCOPE2_BASIS_LABELS } from '../../../shared/utils/reduction-utils'

// Adding a recommendation to the plan. Every pre-filled figure (the saving midpoint, the Scope 2
// basis, the capex midpoint) is editable before it is saved, and the dialog says where each came
// from. One call creates the plan row with the recommendation kept on it as evidence.

const isWholeNumber = (value: string) => value === '' || (Number.isInteger(Number(value)) && Number.isFinite(Number(value)))

export const AcceptRecommendationDialog: FunctionComponent<{
  open: boolean,
  recommendation: IRecommendation | null,
  reportPeriodId: string,
  closeHandler: () => void,
  onAdded: () => void,
}> = ({ open, recommendation, reportPeriodId, closeHandler, onAdded }) => {
  const userNotificationsContext = useContext(UserNotificationsContext)
  const reductionActionService = new ReductionActionService()

  const [annualReductionTce, setAnnualReductionTce] = useState<string>('')
  const [scope2Basis, setScope2Basis] = useState<Scope2Basis>('both')
  const [capexGbp, setCapexGbp] = useState<string>('')
  const [annualOpexDeltaGbp, setAnnualOpexDeltaGbp] = useState<string>('')
  const [lifetimePeriods, setLifetimePeriods] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (open && recommendation) {
      const mid = recommendation.prefill.annualReductionTco2e
      setAnnualReductionTce(mid != null ? String(Math.round(mid * 100) / 100) : '')
      setScope2Basis(recommendation.prefill.scope2Basis || 'both')
      setCapexGbp(recommendation.prefill.capexGbp != null ? String(recommendation.prefill.capexGbp) : '')
      setAnnualOpexDeltaGbp(recommendation.prefill.annualOpexDeltaGbp != null ? String(recommendation.prefill.annualOpexDeltaGbp) : '')
      setLifetimePeriods('')
    }
  }, [open, recommendation])

  if (!recommendation) return null

  const hasSaving = recommendation.saving != null
  const annualValid = annualReductionTce === '' || (Number(annualReductionTce) >= 0 && Number.isFinite(Number(annualReductionTce)))
  const lifetimeValid = lifetimePeriods === '' || (Number.isInteger(Number(lifetimePeriods)) && Number(lifetimePeriods) >= 1)
  const costsValid = isWholeNumber(capexGbp) && isWholeNumber(annualOpexDeltaGbp)
  const isValid = annualValid && lifetimeValid && costsValid

  const costPerTonne = costPerTonnePerYear({
    capexGbp: capexGbp === '' ? null : Number(capexGbp),
    annualOpexDeltaGbp: annualOpexDeltaGbp === '' ? null : Number(annualOpexDeltaGbp),
    annualReductionKg: annualReductionTce === '' ? null : Number(annualReductionTce) * 1000,
    lifetimeYears: lifetimePeriods === '' ? null : Number(lifetimePeriods),
  })

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return
    setIsSubmitting(true)
    try {
      await reductionActionService.acceptRecommendation({
        reportPeriodId,
        actionId: recommendation.actionId,
        entityId: recommendation.entity?.id ?? null,
        annualReduction: annualReductionTce === '' ? null : Number(annualReductionTce) * 1000,
        scope2Basis,
        capexGbp: capexGbp === '' ? null : Number(capexGbp),
        annualOpexDeltaGbp: annualOpexDeltaGbp === '' ? null : Number(annualOpexDeltaGbp),
        reductionLifetimePeriods: lifetimePeriods === '' ? null : Number(lifetimePeriods),
      })
      userNotificationsContext.openSnackbar({ open: true, message: 'Added to your reduction plan' })
      onAdded()
      closeHandler()
    } catch (err: any) {
      const code = err?.response?.data?.code
      const message = code === 1
        ? 'Report period is locked'
        : code === 2
          ? 'This action is already in your reduction plan for this period.'
          : code === 3
            ? 'This recommendation no longer applies to the selected period. Refresh the list and try again.'
            : (err?.response?.data?.message || 'There was an error, please try again')
      userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <DialogWrapper
      open={open}
      onClose={closeHandler}
      title={recommendation.name}
      cancelHandler={closeHandler}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      disableSaveButton={!isValid}
      submitButtonText='Add to plan'
      submitButtonLoadingText='Adding to plan'
      maxWidth='sm'
    >
      <Stack direction='row' spacing={1} sx={{ mb: 1.5 }}>
        <Chip label='Suggested from your data' size='small' variant='outlined' />
        {recommendation.entity?.name && <Chip label={recommendation.entity.name} size='small' variant='outlined' />}
      </Stack>
      <Paper variant='outlined' sx={{ backgroundColor: 'lightGrey.light', p: 2, mb: 2 }}>
        <Typography variant='body2'>{recommendation.reason}</Typography>
        <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}><b>What this assumes.</b> {recommendation.assumption}</Typography>
        {hasSaving && recommendation.saving && (
          <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>
            Suggested range {formatTonnesRange(recommendation.saving.minKg, recommendation.saving.maxKg)}CO<sub>2</sub>e a year. The midpoint is filled in below; change it if you know better.
          </Typography>
        )}
        {recommendation.source.reference && (
          <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>Source: {recommendation.source.reference}</Typography>
        )}
      </Paper>

      <Typography variant='subtitle2' sx={{ mb: 1, color: 'text.secondary' }}>Projection impact</Typography>
      <FormControl fullWidth variant='outlined' sx={{ mt: 1 }} error={!annualValid}>
        <InputLabel htmlFor='accept-annual-reduction'>Annual Reduction</InputLabel>
        <OutlinedInput
          id='accept-annual-reduction'
          label='Annual Reduction'
          type='number'
          value={annualReductionTce}
          onChange={(e) => setAnnualReductionTce(e.target.value)}
          endAdornment={<InputAdornment position='end'><Typography variant='body2' sx={{ color: 'text.secondary' }}>tCO<sub>2</sub>e</Typography></InputAdornment>}
          inputProps={{ step: '0.01', min: 0 }}
        />
        <FormHelperText>
          {!annualValid
            ? 'Enter 0 or more.'
            : hasSaving ? 'Pre-filled with the midpoint of the suggested range. Leave blank to add the action without a figure.' : 'This action improves your data rather than cutting emissions; leave blank or enter your own figure.'}
        </FormHelperText>
      </FormControl>

      {recommendation.scope === '2' && (
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor='accept-scope2-basis'>Scope 2 basis</InputLabel>
          <Select id='accept-scope2-basis' label='Scope 2 basis' value={scope2Basis} onChange={(e) => setScope2Basis(e.target.value as Scope2Basis)}>
            {(Object.keys(SCOPE2_BASIS_LABELS) as Scope2Basis[]).map((basis) => (
              <MenuItem key={basis} value={basis}>{SCOPE2_BASIS_LABELS[basis]}</MenuItem>
            ))}
          </Select>
          <FormHelperText>{SCOPE2_BASIS_HELP[scope2Basis]}</FormHelperText>
        </FormControl>
      )}

      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }} error={!lifetimeValid}>
        <InputLabel htmlFor='accept-lifetime'>Reduction Lifetime (reporting periods)</InputLabel>
        <OutlinedInput
          id='accept-lifetime'
          label='Reduction Lifetime (reporting periods)'
          type='number'
          value={lifetimePeriods}
          onChange={(e) => setLifetimePeriods(e.target.value)}
          placeholder='Ongoing'
          inputProps={{ step: 1, min: 1 }}
        />
        <FormHelperText>{lifetimeValid ? 'Starts in the selected reporting period. Leave blank if ongoing.' : 'Enter a whole number of 1 or more, or leave blank for ongoing.'}</FormHelperText>
      </FormControl>

      <Divider sx={{ my: 3 }} />
      <Typography variant='subtitle2' sx={{ mb: 1, color: 'text.secondary' }}>Cost (optional)</Typography>
      <Stack direction='row' spacing={2} sx={{ mt: 1 }}>
        <FormControl fullWidth variant='outlined' error={!isWholeNumber(capexGbp)}>
          <InputLabel htmlFor='accept-capex'>Capital cost</InputLabel>
          <OutlinedInput
            id='accept-capex'
            label='Capital cost'
            type='number'
            value={capexGbp}
            onChange={(e) => setCapexGbp(e.target.value)}
            startAdornment={<InputAdornment position='start'>£</InputAdornment>}
            inputProps={{ step: 1, min: 0 }}
          />
          <FormHelperText>
            {formatGbpRange(recommendation.cost.capexMinGbp, recommendation.cost.capexMaxGbp)
              ? `Typically ${formatGbpRange(recommendation.cost.capexMinGbp, recommendation.cost.capexMaxGbp)}.`
              : 'One-off, whole pounds.'}
          </FormHelperText>
        </FormControl>
        <FormControl fullWidth variant='outlined' error={!isWholeNumber(annualOpexDeltaGbp)}>
          <InputLabel htmlFor='accept-opex'>Running cost change</InputLabel>
          <OutlinedInput
            id='accept-opex'
            label='Running cost change'
            type='number'
            value={annualOpexDeltaGbp}
            onChange={(e) => setAnnualOpexDeltaGbp(e.target.value)}
            startAdornment={<InputAdornment position='start'>£</InputAdornment>}
            endAdornment={<InputAdornment position='end'><Typography variant='body2' sx={{ color: 'text.secondary' }}>a year</Typography></InputAdornment>}
            inputProps={{ step: 1 }}
          />
          <FormHelperText>Negative for a saving on running costs.</FormHelperText>
        </FormControl>
      </Stack>
      {(costPerTonne != null || formatPaybackRange(recommendation.cost.paybackYearsMin, recommendation.cost.paybackYearsMax)) && (
        <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1.5 }}>
          {costPerTonne != null && <>Cost per tonne: {formatCostPerTonne(costPerTonne)}. </>}
          {formatPaybackRange(recommendation.cost.paybackYearsMin, recommendation.cost.paybackYearsMax) && <>Typical payback {formatPaybackRange(recommendation.cost.paybackYearsMin, recommendation.cost.paybackYearsMax)}.</>}
        </Typography>
      )}
    </DialogWrapper>
  )
}
